/**
 * A stub for the generation layer, standing in for the Server so this surface
 * can be walked in every one of its states with no API key and no network call.
 *
 * There is no ANTHROPIC_API_KEY on this build and none is wanted here. The
 * default mode is `unavailable` — exactly what a deploy with no key reports,
 * and the path that actually runs tonight: `generation.state="unavailable"`,
 * every prose field absent, every slot showing its copyKey, no endpoint error
 * anywhere.
 *
 * The stub also does the Server's job of NOT TRUSTING the model. Everything a
 * pretend model writes here goes through `validateGeneratedText`, which is this
 * file's implementation of generated-text.schema.json `x-validation` — all
 * seven gates, including the two a client cannot evaluate (`paramsProvenance`,
 * `noSecondPersonClaims`). A unit that fails any gate is DISCARDED and stored
 * as null, so the client receives nothing invalid and falls back to keyed copy.
 *
 * The `?generation=` modes exist to prove that. Five of them feed deliberately
 * bad model output, one per gate:
 *
 *   ?generation=hallucinatedFigure  cites a number nothing computed
 *   ?generation=currencySymbol      formats money into the template
 *   ?generation=markup              emits markup
 *   ?generation=badLocale           writes in another base language
 *   ?generation=badPlaceholder      a {slot} with no matching param
 *   ?generation=hedged              refuses / gives regulated advice
 *
 * Each must come back null and render the copyKey. scripts/check-generated-text.mjs
 * asserts exactly that, and `npm test` runs it.
 */
import type {
  AnalysisStatus,
  GeneratedText,
  HealthScore,
  Money,
  Progress,
  Recommendation,
} from '@/generated/types'

export type GenerationMode =
  | 'unavailable'
  | 'pending'
  | 'ready'
  | 'partial'
  | 'failed'
  | 'hallucinatedFigure'
  | 'currencySymbol'
  | 'markup'
  | 'badLocale'
  | 'badPlaceholder'
  | 'hedged'

/** The modes that feed bad output, each aimed at one x-validation gate. */
export const BAD_OUTPUT_MODES: GenerationMode[] = [
  'hallucinatedFigure',
  'currencySymbol',
  'markup',
  'badLocale',
  'badPlaceholder',
  'hedged',
]

const MODES: GenerationMode[] = [
  'unavailable',
  'pending',
  'ready',
  'partial',
  'failed',
  ...BAD_OUTPUT_MODES,
]

export function generationModeFromUrl(): GenerationMode {
  const requested = new URLSearchParams(location.search).get('generation')
  return MODES.includes(requested as GenerationMode) ? (requested as GenerationMode) : 'unavailable'
}

/** Modes where the model is asked to write at all. */
function writes(mode: GenerationMode): boolean {
  return mode === 'ready' || mode === 'partial' || BAD_OUTPUT_MODES.includes(mode)
}

// ───────────────────────────────── x-validation ─────────────────────────────

export type Gate =
  | 'lengthCaps'
  | 'noMarkup'
  | 'noFormattedNumbers'
  | 'placeholdersResolve'
  | 'localeMatch'
  | 'paramsProvenance'
  | 'noSecondPersonClaims'

const FORMATTED_NUMBER = /[$€£¥₹]|\d[\d,.]*\s*(%|USD|EUR|GBP)|\b\d{1,3}([,.]\d{3})+\b/
const MARKUP = /[<>[\]`\n\r]|\p{Extended_Pictographic}/u
const PLACEHOLDER = /\{(\w+)\}/g

/**
 * x-validation noSecondPersonClaims. A refusal or a hedge is a failure, not a
 * result — the copyKey says something true, and a sentence that promises an
 * outcome or gives regulated advice does not.
 */
const HEDGE_OR_ADVICE =
  /\b(I (?:can(?:not|'t)|am unable|do not have)|as an AI|guarantee[ds]?|will definitely|you should invest|tax advice|consult (?:a|your) (?:lawyer|accountant)|APR of)\b/i

/**
 * The deterministic figures a unit is allowed to cite, canonicalised. Built
 * from the payload the deterministic layer already computed — the model picks
 * from this set, it never authors a member of it.
 */
export type Provenance = Set<string>

export const figure = {
  money: (m: Money | null | undefined): string => (m ? `money:${m.amount}:${m.currency}` : 'money:none'),
  count: (n: number | null | undefined): string => `count:${Math.abs(n ?? 0)}`,
  percent: (n: number | null | undefined): string => `percent:${n ?? 0}`,
  month: (s: string): string => `month:${s}`,
  date: (s: string): string => `date:${s}`,
}

/** The canonical form of a param as it arrived, for comparison against Provenance. */
function canonical(param: unknown): string | null {
  if (typeof param !== 'object' || param === null) return null
  const { type, value } = param as { type?: unknown; value?: unknown }
  switch (type) {
    case 'money':
      return figure.money(value as Money)
    case 'count':
      return typeof value === 'number' ? figure.count(value) : null
    case 'percent':
      return typeof value === 'number' ? figure.percent(value) : null
    case 'month':
      return typeof value === 'string' ? figure.month(value) : null
    case 'date':
      return typeof value === 'string' ? figure.date(value) : null
    default:
      return null // `text` is not a figure and carries no provenance obligation
  }
}

const baseLanguage = (tag: string): string => tag.toLowerCase().split(/[-_]/)[0] ?? ''

/**
 * All seven gates. Returns the one that refused, or null when the unit is safe
 * to store. This is what makes the stub a Server and not just a fixture.
 */
export function validateGeneratedText(
  text: GeneratedText,
  provenance: Provenance,
  requestedLocale = 'en-US',
): Gate | null {
  const bullets = text.bullets ?? []
  const params = text.params ?? {}

  if (
    text.template.length === 0 ||
    text.template.length > 400 ||
    bullets.length > 6 ||
    bullets.some((b) => b.length === 0 || b.length > 160) ||
    Object.keys(params).length > 8
  ) {
    return 'lengthCaps'
  }

  const strings = [text.template, ...bullets]
  if (strings.some((s) => MARKUP.test(s))) return 'noMarkup'
  if (strings.some((s) => FORMATTED_NUMBER.test(s))) return 'noFormattedNumbers'
  if (strings.some((s) => HEDGE_OR_ADVICE.test(s))) return 'noSecondPersonClaims'
  if (baseLanguage(text.locale) !== baseLanguage(requestedLocale)) return 'localeMatch'

  for (const s of strings) {
    for (const [, name] of s.matchAll(PLACEHOLDER)) {
      if (!(name in params)) return 'placeholdersResolve'
    }
  }

  // paramsProvenance — the gate that stops a hallucinated figure. Every
  // money/date/percent/count param must equal something already computed.
  for (const param of Object.values(params)) {
    const key = canonical(param)
    if (key === null) continue
    if (!provenance.has(key)) return 'paramsProvenance'
  }

  return null
}

/** Store the unit, or null if any gate refused. Failures are counted, never surfaced. */
export const discarded: { count: number; lastGate: Gate | null } = { count: 0, lastGate: null }

function store(text: GeneratedText, provenance: Provenance): GeneratedText | null {
  const gate = validateGeneratedText(text, provenance)
  if (gate) {
    discarded.count += 1
    discarded.lastGate = gate
    return null
  }
  return text
}

// ────────────────────────────── the pretend model ───────────────────────────

/** A stable 16-hex digest of the inputs, standing in for the real sourceHash. */
export function sourceHash(seed: string): string {
  let h1 = 0x811c9dc5
  let h2 = 0x01000193
  for (let i = 0; i < seed.length; i += 1) {
    h1 = Math.imul(h1 ^ seed.charCodeAt(i), 0x01000193) >>> 0
    h2 = Math.imul(h2 + seed.charCodeAt(i) + 1, 0x85ebca6b) >>> 0
  }
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).slice(0, 16)
}

const GENERATED_AT = '2026-08-09T20:00:00.000Z'

/** What a mode does to an otherwise well-formed unit. */
function corrupt(mode: GenerationMode, text: GeneratedText): GeneratedText {
  switch (mode) {
    case 'hallucinatedFigure':
      // A figure nothing computed. Well-formed, plausible, and wrong — the
      // exact failure paramsProvenance exists for.
      return { ...text, params: { ...text.params, saved: { type: 'money', value: { amount: 999_99, currency: 'USD' } } }, template: `${text.template} That is {saved} you did not lose.` }
    case 'currencySymbol':
      return { ...text, template: `${text.template} You kept $1,240.00 last month.` }
    case 'markup':
      return { ...text, template: `${text.template} See <b>the breakdown</b>.` }
    case 'badLocale':
      return { ...text, locale: 'fr-FR' }
    case 'badPlaceholder':
      return { ...text, template: `${text.template} It moved most in {monthNobodyComputed}.` }
    case 'hedged':
      return { ...text, template: 'I cannot provide tax advice, but I can guarantee this will improve.' }
    default:
      return text
  }
}

function unit(
  mode: GenerationMode,
  provenance: Provenance,
  seed: string,
  template: string,
  params: GeneratedText['params'],
  bullets?: string[],
): GeneratedText | null {
  const written: GeneratedText = {
    template,
    ...(bullets ? { bullets } : {}),
    params,
    locale: 'en-US',
    sourceHash: sourceHash(seed),
    generatedAt: GENERATED_AT,
    model: 'stub-local',
  }
  return store(corrupt(mode, written), provenance)
}

/**
 * The status two rows would report. `unavailable` is never retryable: an
 * absent key is a settled state, not a failure waiting to be retried.
 */
export function generationStatus(
  mode: GenerationMode,
  monthsOfData: number,
  poll: number,
): AnalysisStatus['generation'] {
  // `pending` settles on the third poll, so the swap-in can be watched rather
  // than reasoned about.
  const settled = mode === 'pending' && poll >= 3
  const state: AnalysisStatus['generation']['state'] = settled
    ? 'ready'
    : mode === 'pending'
      ? 'pending'
      : mode === 'partial'
        ? 'partial'
        : writes(mode)
          ? // A run whose every unit was discarded produced nothing usable.
            BAD_OUTPUT_MODES.includes(mode)
            ? 'failed'
            : 'ready'
          : mode === 'unavailable'
            ? 'unavailable'
            : 'failed'

  return {
    state,
    completedAt: state === 'pending' ? null : GENERATED_AT,
    failureReason:
      mode === 'unavailable'
        ? 'notConfigured'
        : BAD_OUTPUT_MODES.includes(mode) || mode === 'failed'
          ? 'invalidOutput'
          : null,
    // An absent key is never retried. Invalid output is worth one more try.
    retryable: mode === 'failed' || BAD_OUTPUT_MODES.includes(mode),
    sourceHash: state === 'ready' || state === 'partial' ? sourceHash(`analysis:${monthsOfData}`) : null,
  }
}

// ──────────────────────────── payload decoration ────────────────────────────

export function scoreProvenance(score: HealthScore): Provenance {
  const p: Provenance = new Set([
    figure.count(score.score),
    figure.count(score.previousScore),
    figure.count(score.delta),
    figure.date(score.periodEnd ?? ''),
  ])
  for (const pillar of score.pillars) {
    p.add(figure.count(pillar.score))
    p.add(figure.count(pillar.delta))
    p.add(figure.percent(pillar.value))
    p.add(figure.percent(pillar.target))
  }
  return p
}

export function withScoreNarrative(score: HealthScore, mode: GenerationMode): HealthScore {
  if (!writes(mode)) return score

  const provenance = scoreProvenance(score)
  const seed = `score:${score.score}:${score.periodEnd}`
  const worst = [...score.pillars].sort((a, b) => a.score - b.score)[0]

  /**
   * The weakest pillar's SCORE, not its id.
   *
   * A `text` param is printed by the client verbatim, so it may only ever hold
   * something already user-facing — a merchant, an account or a category name,
   * per the schema. A pillar id is an internal enum whose user-facing name
   * lives in copy.json under `healthScore.pillar.<id>`, and the Server does not
   * localize, so there is no way to put that name in a param. The model has to
   * word around it, and the section header the line sits under already says
   * which pillar it is.
   */
  const weakestScore = { type: 'count' as const, value: worst?.score ?? 0 }

  const summary = unit(mode, provenance, `${seed}:summary`, 'Your score sits at {score} out of a hundred, held back by one area scoring {weakest}.', {
    score: { type: 'count', value: score.score },
    weakest: weakestScore,
  })

  // `partial` is the case where one unit passed and another did not: whatMoved
  // lands, whatToDo does not, and the screen shows one of each without saying so.
  const whatMoved = unit(mode, provenance, `${seed}:whatMoved`, 'It moved {delta} points since the last check, mostly on steadier month-to-month spending.', {
    delta: { type: 'count', value: Math.abs(score.delta ?? 0) },
  })

  const whatToDo =
    mode === 'partial'
      ? null
      : unit(mode, provenance, `${seed}:whatToDo`, 'The first of these is worth the most, and none of them need a change to your income.', {})

  const landed = [summary, whatMoved, whatToDo].some(Boolean)
  return {
    ...score,
    narrative: { ...(summary ? { summary } : {}), ...(whatMoved ? { whatMoved } : {}), ...(whatToDo ? { whatToDo } : {}) },
    narrativeState: landed ? 'ready' : 'failed',
  }
}

export function withProgressNarrative(progress: Progress, mode: GenerationMode): Progress {
  if (!writes(mode)) return progress

  const first = progress.points[0]
  const last = progress.points[progress.points.length - 1]
  if (!first || !last) return progress

  const provenance: Provenance = new Set([
    figure.month(first.month),
    figure.month(last.month),
    figure.count(progress.scoreChange),
    figure.count(progress.actionsTaken),
    figure.money(progress.savedToDate),
  ])

  const summary = unit(
    mode,
    provenance,
    `progress:${progress.range}:${last.month}`,
    'Since {start} your score has moved {change} points, and you have put back {saved} a month.',
    {
      start: { type: 'month', value: first.month },
      change: { type: 'count', value: Math.abs(progress.scoreChange ?? 0) },
      saved: { type: 'money', value: progress.savedToDate },
    },
  )

  return {
    ...progress,
    narrative: summary ? { summary } : null,
    narrativeState: summary ? 'ready' : 'failed',
  }
}

/**
 * Prose on the top three recommendations only, per Decision 5 — the rest keep
 * their copyKey, which is what a bounded run looks like in a list.
 */
export function withRecommendationProse(rows: Recommendation[], mode: GenerationMode): Recommendation[] {
  if (!writes(mode)) return rows

  return rows.map((row, index) => {
    if (index >= 3) return row
    // `partial`: the middle card's prose was discarded and shows keyed copy,
    // beside two cards that have prose. Neither card says which it is.
    if (mode === 'partial' && index === 1) return { ...row, proseState: 'failed' as const }

    const provenance: Provenance = new Set([
      figure.money(row.monthlyImpact),
      figure.money(row.annualImpact),
      figure.count(row.priority),
    ])
    const seed = `rec:${row.id}`
    const impact = { type: 'money' as const, value: row.monthlyImpact ?? { amount: 0, currency: 'USD' } }

    const why = unit(mode, provenance, `${seed}:why`, 'This is worth {impact} a month, the largest single change available to you right now.', { impact })
    const steps = unit(mode, provenance, `${seed}:steps`, 'Three steps, in order.', { impact }, [
      'Open the subscription list and mark what you no longer use.',
      'Cancel from the provider, not from the bank.',
      'Check next month that the charge stopped.',
    ])

    const landed = Boolean(why || steps)
    return {
      ...row,
      prose: { ...(why ? { why } : {}), ...(steps ? { steps } : {}) },
      proseState: landed ? ('ready' as const) : ('failed' as const),
    }
  })
}
